import React, { useState, useRef, useEffect } from "react";
import {
  Mail,
  Phone,
  Send,
} from "lucide-react";
import { getUserData } from "../../api/storageService";
import { appointmentInquiry } from "../../api/api";

const SupportEnquiry = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    notes: "", 
  });
  
  const [loading, setLoading] = useState(false);

  // Custom Toaster Function (same as BookConsultForm)
  const showToast = (message, type = 'success') => {
    const existingToast = document.querySelector('.custom-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = `custom-toast toast-${type}`;

    const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';

    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
          ${icon}
        </div>
        <div style="flex: 1;">${message}</div>
        <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
      </div>
    `;

    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: ${bgColor};
      color: white;
      padding: 14px 20px;
      border-radius: 12px;
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
      min-width: 300px;
      max-width: 400px;
      cursor: pointer;
      animation: slideInRight 0.3s ease;
      z-index: 10000;
    `;

    if (!document.querySelector('#toast-styles')) {
      const style = document.createElement('style');
      style.id = 'toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    }, 3000);

    toast.onclick = () => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    };
  };

  useEffect(() => {
    const loadUserData = () => {
      try {
        const userData = getUserData();
        
        if (userData) {
          setFormData(prev => ({
            ...prev,
            fullName: userData.name || "",
            email: userData.email || "",
            phone: userData.phone || "",
          }));
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };
    
    loadUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    if (!formData.subject.trim()) {
      showToast("Please enter a subject", "error");
      return false;
    }
    if (!formData.notes.trim()) { 
      showToast("Please enter your message", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const inquiryData = {
        subject: formData.subject,
        notes: formData.notes, 
      };
      
      const response = await appointmentInquiry(inquiryData);
      
      if (response && response.status === true) {
        showToast("Message sent successfully! Our team will get back to you soon.", "success");
        setFormData(prev => ({
          ...prev,
          subject: "",
          notes: "", 
        }));
      } else {
        showToast(response?.message || "Failed to send message. Please try again.", "error");
      }
    } catch (err) {
      console.error("Error submitting inquiry:", err);
      showToast(err.message || "Failed to send message. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-lato">
      <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
        Support / Enquiry
      </h2>
      <p className="text-[#97a09a] text-sm mb-8">
        Have a question or need assistance? Reach out to our dedicated support
        team.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5 sm:p-6">
          <h3 className="text-[18px] font-semibold text-[#24312d] mb-6">
            Send us a message
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label className="block text-[13px] font-medium text-[#24312d] mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full h-[46px] border border-[#e1e6e1] rounded-lg px-4 text-[14px] text-[#24312d] outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a] bg-[#f7f9f7]"
                readOnly
              />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#24312d] mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full h-[46px] border border-[#e1e6e1] rounded-lg px-4 text-[14px] text-[#24312d] outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a] bg-[#f7f9f7]"
                readOnly
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
              <label className="block text-[13px] font-medium text-[#24312d] mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full h-[46px] border border-[#e1e6e1] rounded-lg px-4 text-[14px] text-[#24312d] outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a] bg-[#f7f9f7]"
                readOnly
              />
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-[13px] font-medium text-[#24312d] mb-2">
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Briefly describe your issue"
              className="w-full h-[46px] border border-[#e1e6e1] rounded-lg px-4 text-[14px] text-[#24312d] placeholder-[#b1b8b3] outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a]"
              required
            />
          </div>

          <div className="mb-5">
            <label className="block text-[13px] font-medium text-[#24312d] mb-2">
              Message
            </label>
            <textarea
              name="notes" 
              value={formData.notes} 
              onChange={handleInputChange}
              rows="6"
              placeholder="Please provide as much detail as possible so we can better assist you..."
              className="w-full border border-[#e1e6e1] rounded-lg px-4 py-3 text-[14px] text-[#24312d] placeholder-[#b1b8b3] resize-none outline-none focus:border-[#57b26a] focus:ring-1 focus:ring-[#57b26a]"
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="h-[42px] px-6 bg-[#57b26a] text-white rounded-lg text-[14px] font-medium flex items-center gap-2 transition hover:bg-[#49aa5d] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={15} />
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>

        <div className="bg-white border border-[#e8ece8] rounded-xl p-5 sm:p-6 h-fit">
          <h3 className="text-[18px] font-semibold text-[#24312d] mb-6">
            Contact Information
          </h3>
          <div className="flex gap-4 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#f7f9f7] flex items-center justify-center shrink-0">
              <Mail size={18} className="text-[#57b26a]" />
            </div>
            <div>
              <p className="text-[14px] font-medium text-[#24312d]">
                Email Support
              </p>
              <p className="text-[13px] text-[#97a09a] mt-1">
                info@nutrillion.one
              </p>
              <p className="text-[12px] text-[#97a09a] mt-1">
                Available 24/7
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-[#f7f9f7] flex items-center justify-center shrink-0">
              <Phone size={18} className="text-[#57b26a]" />
            </div>
            <div>
              <p className="text-[14px] font-medium text-[#24312d]">
                Phone Support
              </p>
              <p className="text-[13px] text-[#97a09a] mt-1">
                9557439399
              </p>
              <p className="text-[12px] text-[#97a09a] mt-1">
                Mon - Sat, 10am - 7pm IST
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportEnquiry;